import moment, { Moment } from 'moment-timezone';
import * as DocumentPicker from 'expo-document-picker';
import { Container, SafeAreaView, ScrollView, SxProp, View } from 'dripsy';
import React, { useCallback, useEffect, useState } from 'react';

import { PageContainer } from '@/features/earn/components/page/PageContainer';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { DevToolsPageHeader } from '../components/DevToolsPageHeader';
import { DevToolsStackRoutes, DevToolsStackScreenProps } from '../types';
import { H6, TextBody, TextTitle } from '@/design-system/components/typography';
import { Dropdown } from '@/design-system/components/dropdown';
import { UniCheckbox, UniInput, UniInputAmount, UniOtpInput, UniSwitch, UniUploader } from '@/design-system/components/inputs';
import { UniInputVariant } from '@/design-system/components/inputs/useInputTheme.hook';
import { AccountSelector } from '@/design-system/components/accountSelector/AccountSelector';
import { useWallet } from '@/utils/useWallet.hook';
import { DateRangePicker } from '@/design-system/components/dateRangePicker';
import { useDebounce } from '@/utils/useDebounce.hook';
import { OtpInputVariant } from '@/design-system/components/inputs/uniOtpInput';
import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { DocumentUploader, DocumentUploaderProps } from '@/design-system/components/uploadDocument/DocumentUploader';
import { Input, InputProps } from '@/design-system/components/input/input/Input';
import { INPUT_SIZE, INPUT_VARIANT } from '@/design-system/components/input/types';
import { SVGIconList, SVGIconListArray } from '@/design-system/components/icons/Icon';
import { SearchBar } from '@/design-system/components/input/input/SearchBar';
import { CurrencySelector } from '@/design-system/components/currencySelector/CurrencySelector';
import { FileUploader } from '@/design-system/components/fileUploader/FileUploader';

export const DevToolsInput: React.FC<DevToolsStackScreenProps<DevToolsStackRoutes.Input>> = ({}) => {
  const [dropdownValue, setDropdownValue] = useState('apple');
  const handleChangeDropdownValue = (value: string) => {
    setDropdownValue(value);
  };

  // Input
  const [inputProps, setInputProps] = useState<InputProps>({
    onValueChange: (value?: string) => {},
    label: '',
    value: 'This is default value',
    errorText: '',
    disabled: false,
    prefixText: '',
    prefixIcon: undefined,
    suffixText: '',
    suffixIcon: undefined,
    allowClear: false,
    size: INPUT_SIZE.MEDIUM,
    variant: INPUT_VARIANT.ROUNDED,
    placeholder: ''
  });
  // Account select
  const wallet = useWallet({ noSave: true });
  const [accountSelectorValues, setAccountSelectorValues] = useState({
    asset: 'USD',
    walletId: ''
  });

  // UniCheckbox
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  // Date Range Picker
  const [dateRangeProps, setDateRangeProps] = useState<{ from: Moment | null; to: Moment | null }>({
    from: null,
    to: null
  });

  const handleChangeDateRange = (value: { from: Moment | null; to: Moment | null }) => {
    setDateRangeProps(value);
  };

  // UniSwitch
  const [switchChecked, setSwitchChecked] = useState(false);
  const handleChangeSwitch = (value: boolean) => setSwitchChecked(value);

  // AccountSelector
  const [accountSelectorWalletId, setAccountSelectorWalletId] = useState('');
  const handleChangeAccountSelector = (value: string) => setAccountSelectorWalletId(value);

  // OTP Input
  const [otpDropdownValue, setOtpDropdownValue] = useState<OtpInputVariant>(OtpInputVariant.Square);
  const handleChangeOtpDropdownValue = (value: OtpInputVariant) => {
    setOtpDropdownValue(value);
  };
  const CORRECT_OTP = '123456';
  const [otpValue, setOtpValue] = useState('');
  const [otpErrorText, setOtpErrorText] = useState<string | null>(null);
  const handleOtpChange = (value: string) => {
    setOtpValue(value);
  };
  const debounceOtp = useDebounce(otpValue, 250);

  const checkOtp = () => {
    if (debounceOtp.length < 6) return 'OTP must be 6 digits.';
    if (debounceOtp !== CORRECT_OTP) return 'OTP is incorrect.';
    return null;
  };
  useEffect(() => {
    setOtpErrorText(checkOtp());
  }, [debounceOtp]);

  // Document Uploader
  const [documentUploaderSelectedDocument, setDocumentUploaderSelectedDocument] = useState<any>(null);
  const [documentUploaderProps, setDocumentUploaderProps] = useState<DocumentUploaderProps>({
    title: undefined,
    instructions: undefined,
    onValueChange: (document: undefined | DocumentPicker.DocumentResult) => {
      setDocumentUploaderSelectedDocument(document);
    },
    isLoading: undefined,
    isUploadSuccess: undefined
  });

  const handleChangeUniUploaderProps = useCallback(
    (key: keyof DocumentUploaderProps, value: DocumentUploaderProps[keyof DocumentUploaderProps]) => {
      setDocumentUploaderProps((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  useEffect(() => {
    if (!documentUploaderSelectedDocument) return;
    handleChangeUniUploaderProps(
      'instructions',
      'You can upload again by drogging a new file or clicking here. The current file will be replaced with the newly uploaded file.'
    );
  }, [documentUploaderSelectedDocument]);

  return (
    <SafeAreaView sx={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <PageContainer sx={{ paddingX: '$10', paddingY: '$10', backgroundColor: 'transparent' }}>
          <DevToolsPageHeader currentPage={DevToolsStackRoutes.Input} />
          <View sx={{ gap: '$5' }}>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Dropdown
              </H6>
              {/* Button Size */}
              <View sx={{ zIndex: 1999 }}>
                <Dropdown
                  label="Button Size"
                  options={[
                    { label: 'Apple', value: 'apple' },
                    { label: 'Orange', value: 'orange' },
                    { label: 'Lemon', value: 'lemon' },
                    { label: 'Strawberry', value: 'strawberry' }
                  ]}
                  value={dropdownValue}
                  onValueChange={(value) => handleChangeDropdownValue('apple')}
                />
              </View>
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
<Dropdown
  label="Button Size"
  options={[
    { label: 'Apple', value: 'apple' },
    { label: 'Orange', value: 'orange' },
    { label: 'Lemon', value: 'lemon' },
    { label: 'Strawberry', value: 'strawberry' }
  ]}
  onValueChange={(value) => handleChangeDropdownValue('apple')}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Input
              </H6>
              <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
                <View sx={{ flexDirection: 'row', gap: '$2', zIndex: 3000 }}>
                  <Dropdown
                    label="variant"
                    options={[
                      { label: 'INPUT_VARIANT.SQUARED', value: INPUT_VARIANT.SQUARED },
                      { label: 'INPUT_VARIANT.ROUNDED', value: INPUT_VARIANT.ROUNDED },
                      { label: 'INPUT_VARIANT.UNDERLINED', value: INPUT_VARIANT.UNDERLINED }
                    ]}
                    value={inputProps.variant}
                    onValueChange={(value) => setInputProps((prev) => ({ ...prev, variant: value as INPUT_VARIANT }))}
                  />
                  <Dropdown
                    label="size"
                    options={[
                      { label: 'INPUT_SIZE.EXTRA_SMALL', value: INPUT_SIZE.EXTRA_SMALL },
                      { label: 'INPUT_SIZE.SMALL', value: INPUT_SIZE.SMALL },
                      { label: 'INPUT_SIZE.MEDIUM', value: INPUT_SIZE.MEDIUM },
                      { label: 'INPUT_SIZE.LARGE', value: INPUT_SIZE.LARGE }
                    ]}
                    value={inputProps.size}
                    onValueChange={(value) => setInputProps((prev) => ({ ...prev, size: value as INPUT_SIZE }))}
                  />
                  <Dropdown
                    label="prefixIcon"
                    options={[{ label: 'undefined', value: '' }, ...SVGIconListArray.map((icon) => ({ label: icon, value: icon }))]}
                    value={inputProps.prefixIcon}
                    onValueChange={(value) =>
                      setInputProps((prev) => ({ ...prev, prefixIcon: value ? (value as SVGIconList) : undefined }))
                    }
                  />
                  <Dropdown
                    label="suffixIcon"
                    options={[{ label: 'undefined', value: '' }, ...SVGIconListArray.map((icon) => ({ label: icon, value: icon }))]}
                    value={inputProps.suffixIcon}
                    onValueChange={(value) =>
                      setInputProps((prev) => ({ ...prev, suffixIcon: value ? (value as SVGIconList) : undefined }))
                    }
                  />
                </View>
                <View sx={{ flexDirection: 'row', gap: '$2', zIndex: 2999 }}>
                  <Input label="label" onValueChange={(value) => setInputProps((prev) => ({ ...prev, label: value }))} />
                  <Input label="errorText" onValueChange={(value) => setInputProps((prev) => ({ ...prev, errorText: value }))} />
                  <Input label="prefixText" onValueChange={(value) => setInputProps((prev) => ({ ...prev, prefixText: value }))} />
                  <Input label="suffixText" onValueChange={(value) => setInputProps((prev) => ({ ...prev, suffixText: value }))} />
                </View>
                <TextTitle semiBold>Preview</TextTitle>
                <Input {...inputProps} onValueChange={(value) => setInputProps((prev) => ({ ...prev, value }))} />
              </PageItem>
              <Input onValueChange={(value) => console.log('@onValueChange', value)} size={INPUT_SIZE.EXTRA_SMALL} />
              <Input onValueChange={(value) => console.log('@onValueChange', value)} label="Label" />
              <Input
                onValueChange={(value) => console.log('@onValueChange', value)}
                size={INPUT_SIZE.MEDIUM}
                prefixIcon="placeholderLight20"
                errorText="Error Text Here"
              />
              <Input
                onValueChange={(value) => console.log('@onValueChange', value)}
                size={INPUT_SIZE.MEDIUM}
                prefixText="Label"
                allowClear
              />
              <Input
                onValueChange={(value) => console.log('@onValueChange', value)}
                size={INPUT_SIZE.MEDIUM}
                prefixText="Diabled"
                disabled
              />
            </PageItem>
            {/* SEARCH BAR */}
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                SearchBar
              </H6>
              <SearchBar onValueChange={(value) => console.log('@onValueChange', value)} placeholder="SearchBar placeholder" />
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
<SearchBar onValueChange={(value) => console.log('@onValueChange', value)} />
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                UniInput
              </H6>
              <UniInput labelText="Button Label" />
              <UniInput labelText="Button Label" value="This is default value" />
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                UniInputAmount
              </H6>
              <UniInputAmount
                variant={UniInputVariant.v1}
                labelText="labelText"
                labelRightText="labelRightText"
                balance={999}
                asset="USD"
                onValueChange={(value) => {}}
                max={999}
              />
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Currency Selector
              </H6>
              <CurrencySelector onValueChange={(value) => console.log(value)} sx={{ mb: 0 }} />
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
<CurrencySelector
  onValueChange={(value) => console.log(value)}
  sx={{ mb: 0 }}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Account Selector
              </H6>
              <AccountSelector
                onValueChange={(value) => setAccountSelectorValues({ ...accountSelectorValues, walletId: value })}
                sx={{ mb: 0 }}
              />
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
<AccountSelector
  asset={accountSelectorValues.asset}
  onValueChange={(value) => setAccountSelectorValues({ ...accountSelectorValues, walletId: value })}
  value={accountSelectorValues.walletId}
  sx={{ mb: 0 }}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                DateRangePicker
              </H6>
              <View sx={{ width: 'fit-content' }}>
                <DateRangePicker
                  dateRangeProps={dateRangeProps}
                  onValueChange={(value) =>
                    handleChangeDateRange({ from: value.from ? moment(value.from) : null, to: value.to ? moment(value.to) : null })
                  }
                />
              </View>
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
const [dateRangeProps, setDateRangeProps] = useState<{ from: Moment | null; to: Moment | null }>({
  from: null,
  to: null
});

const handleChangeDateRange = (value: { from: Moment | null; to: Moment | null }) => {
  setDateRangeProps(value);
};

<DateRangePicker
  dateRangeProps={dateRangeProps}
  onValueChange={(value) => handleChangeDateRange({ from: value.from ? moment(value.from) : null, to: value.to ? moment(value.to) : null })}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                UniSwitch
              </H6>
              <View sx={{ width: 'fit-content' }}>
                <UniSwitch value={switchChecked} onValueChange={handleChangeSwitch} disabled={false} />
              </View>
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
const [switchChecked, setSwitchChecked] = useState(false);
const handleChangeSwitch = (value: boolean) => setSwitchChecked(value);

<UniSwitch value={switchChecked} onValueChange={handleChangeSwitch} disabled={false} />
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Account Selector
              </H6>
              <AccountSelector asset={undefined} onValueChange={handleChangeAccountSelector} value={accountSelectorWalletId} />
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
const [accountSelectorWalletId, setAccountSelectorWalletId] = useState('');
const handleChangeAccountSelector = (value: string) => setAccountSelectorWalletId(value);

<AccountSelector
  asset={undefined}
  onValueChange={handleChangeAccountSelector}
  value={accountSelectorWalletId}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                UniCheckbox
              </H6>
              <UniCheckbox
                label="Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi tempus justo id tempus pulvinar. Maecenas at dui suscipit, suscipit arcu sed, maximus mi. Sed efficitur purus nec commodo dictum. Ut vitae aliquam sem, at dapibus nulla. Vivamus ut efficitur nulla. Ut consectetur tincidunt vulputate. Nam sed nisi magna. Nunc blandit nibh ut elit cursus varius."
                value={checkboxChecked}
                onValueChange={(value) => setCheckboxChecked(value)}
                options={{
                  labelSecondaryColor: true
                }}
                sx={{}}
                disabled={false}
              />
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`

const labelText = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi tempus justo id tempus pulvinar. Maecenas at dui suscipit, suscipit arcu sed, maximus mi. Sed efficitur purus nec commodo dictum. Ut vitae aliquam sem, at dapibus nulla. Vivamus ut efficitur nulla. Ut consectetur tincidunt vulputate. Nam sed nisi magna. Nunc blandit nibh ut elit cursus varius."

<UniCheckbox
  label={labelText}
  value={checkboxChecked}
  onValueChange={(value) => setCheckboxChecked(value)}
  options={{
    labelSecondaryColor: true
  }}
  sx={{}}
  disabled={false}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>

            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                UniOtpInput
              </H6>
              <SegmentSelect
                value={otpDropdownValue}
                onValueChange={handleChangeOtpDropdownValue}
                data={[
                  { labelKey: 'Square', value: OtpInputVariant.Square },
                  { labelKey: 'Rounded', value: OtpInputVariant.Rounded },
                  { labelKey: 'Underline', value: OtpInputVariant.Underline }
                ]}
              />
              <Container>
                <UniOtpInput onValueChange={handleOtpChange} errorText={undefined} variant={otpDropdownValue} />
              </Container>
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
const CORRECT_OTP = '123456';
const [otpValue, setOtpValue] = useState('');
const [otpErrorText, setOtpErrorText] = useState<string | null>(null);
const handleOtpChange = (value: string) => {
  setOtpValue(value);
};
const debounceOtp = useDebounce(otpValue, 250);

const checkOtp = () => {
  if (debounceOtp.length < 6) return 'OTP must be 6 digits.';
  if (debounceOtp !== CORRECT_OTP) return 'OTP is incorrect.';
  return null;
};
useEffect(() => {
  setOtpErrorText(checkOtp());
}, [debounceOtp]);

<UniOtpInput onValueChange={handleOtpChange} errorText={otpErrorText} variant={OtpInputVariant.${otpDropdownValue}} />;
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>

            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 999 }}>
              <H6 semiBold label>
                Document Uploader
              </H6>
              <UniInput
                labelText="Document Uploader Title"
                value={documentUploaderProps.title}
                onValueChange={(value) => handleChangeUniUploaderProps('title', value)}
              />
              <UniInput
                labelText="Document Uploader Instructions"
                value={documentUploaderProps.instructions}
                onValueChange={(value) => handleChangeUniUploaderProps('instructions', value)}
              />
              <Container>
                <DocumentUploader
                  title={documentUploaderProps.title}
                  instructions={documentUploaderProps.instructions}
                  onValueChange={setDocumentUploaderSelectedDocument}
                  isLoading={documentUploaderProps.isLoading}
                  isUploadSuccess={documentUploaderSelectedDocument ? true : false}
                />
              </Container>
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
<DocumentUploader
  title={${documentUploaderProps.title ? `"${documentUploaderProps.title}"` : 'undefined'}}
  instructions={${documentUploaderProps.instructions ? `"${documentUploaderProps.instructions}"` : 'undefined'}}
  onValueChange=(document: undefined | DocumentPicker.DocumentResult) => {{}}
  isLoading={${documentUploaderProps.isLoading ? 'true' : 'false'}}
  isUploadSuccess={${documentUploaderSelectedDocument ? 'true' : 'false'}}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>

            {/* UPLOADER */}
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Uploader
              </H6>
              <View sx={{ width: 'fit-content', gap: '$5' }}>
                <FileUploader
                  label="Label"
                  uploadURL=""
                  onValueChange={() => {}}
                  onSuccess={() => {}}
                  onError={() => {}}
                  sx={{ height: 300, width: 500 }}
                />
                <FileUploader
                  label="Label"
                  uploadURL=""
                  variant="rounded"
                  onValueChange={() => {}}
                  onSuccess={() => {}}
                  onError={() => {}}
                  sx={{ height: 300, width: 500 }}
                />
              </View>
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
<FileUploader
  label="Label"
  uploadURL=""
  onValueChange={() => {}}
  onSuccess={() => {}}
  onError={() => {}}
  sx={{ height: 300, width: 500 }}
/>
<FileUploader
  label="Label"
  uploadURL=""
  variant="rounded"
  onValueChange={() => {}}
  onSuccess={() => {}}
  onError={() => {}}
  sx={{ height: 300, width: 500 }}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
          </View>
        </PageContainer>
      </ScrollView>
    </SafeAreaView>
  );
};

enum StyleName {
  container = 'container',
  pageItem = 'pageItem'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  pageItem: {
    width: '100%',
    padding: '$7',
    borderRadius: 10,
    gap: '$2',
    zIndex: -100
  }
};
